import {
  GridFilterModel,
  GridSortModel,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  AdvancedSearchQuery,
  SearchRule,
  FieldConfig,
  FieldOperator,
} from '../components/TableSearch/types';
import { Column } from '../components/LgmDataTable';

/**
 * Maps DataGrid filter operators to our FieldOperator type
 */
function mapGridOperatorToFilterOperator(gridOp: string): FieldOperator {
  const mapping: Record<string, FieldOperator> = {
    contains: 'contains',
    equals: 'equals',
    notEquals: 'notEquals', // Fixed: was missing this exact match
    startsWith: 'contains', // Map to contains as closest match
    endsWith: 'contains', // Map to contains as closest match
    '!=': 'notEquals',
    not: 'notEquals',
    '>': 'greaterThan',
    '<': 'lessThan',
    after: 'isAfter',
    before: 'isBefore',
  };

  return mapping[gridOp] || 'contains';
}

/**
 * Converts DataGrid's GridFilterModel to our AdvancedSearchQuery format
 * This allows DataGrid column filters to work with our existing server-side API
 */
export function gridFilterToAdvancedQuery(
  filterModel: GridFilterModel,
  _: FieldConfig[]
): AdvancedSearchQuery {
  if (!filterModel.items || filterModel.items.length === 0) {
    return {
      filterGroups: [],
      groupOperator: 'AND',
    };
  }

  const rules: SearchRule[] = filterModel.items
    .filter(item => {
      // Filter out items without value or with empty string value
      if (item.value === undefined || item.value === null) return false;
      if (typeof item.value === 'string' && item.value.trim() === '')
        return false;
      return true;
    })
    .map((item, index) => {
      return {
        id: `grid_filter_${index}`,
        field: item.field,
        operator: mapGridOperatorToFilterOperator(item.operator || 'contains'),
        value: item.value,
      };
    });

  // If no valid rules after filtering, return empty query
  if (rules.length === 0) {
    return {
      filterGroups: [],
      groupOperator: 'AND',
    };
  }

  return {
    filterGroups: [
      {
        id: 'grid_filters',
        logicalOperator: filterModel.logicOperator === 'or' ? 'OR' : 'AND',
        rules: rules,
      },
    ],
    groupOperator: 'AND',
  };
}

/**
 * Merges two AdvancedSearchQuery objects with AND logic
 * Used when combining TableSearch advanced filters with DataGrid column filters
 */
export function mergeAdvancedQueries(
  query1: AdvancedSearchQuery,
  query2: AdvancedSearchQuery
): AdvancedSearchQuery {
  // If one is empty, return the other
  if (!query1 || query1.filterGroups.length === 0) return query2;
  if (!query2 || query2.filterGroups.length === 0) return query1;

  // Merge groups with AND combinator
  return {
    filterGroups: [...query1.filterGroups, ...query2.filterGroups],
    groupOperator: 'AND',
  };
}

/**
 * Converts our sort config to DataGrid's GridSortModel
 */
export function sortConfigToGridSortModel<T>(
  sortConfig: {
    key: keyof T;
    direction: 'ASC' | 'DESC';
  } | null
): GridSortModel {
  if (!sortConfig) return [];
  return [
    {
      field: String(sortConfig.key),
      sort: sortConfig.direction.toLowerCase() as 'asc' | 'desc',
    },
  ];
}

/**
 * Converts DataGrid's GridSortModel to our sort config
 */
export function gridSortModelToSortConfig<T>(
  sortModel: GridSortModel
): { key: keyof T; direction: 'ASC' | 'DESC' } | null {
  if (!sortModel || sortModel.length === 0) return null;
  const firstSort = sortModel[0];
  return {
    key: firstSort.field as keyof T,
    direction: firstSort.sort === 'desc' ? 'DESC' : 'ASC',
  };
}

/**
 * Transforms our Column type to DataGrid's GridColDef
 * Preserves custom rendering and tooltip functionality
 */
export function transformToGridColDef<T>(
  column: Column<T>,
  renderCell?: (params: GridRenderCellParams) => React.ReactNode
): GridColDef {
  // Parse width - convert percentage to pixels estimate (assumes 1400px container)
  let widthValue: number | undefined;
  if (typeof column.width === 'string' && column.width.endsWith('%')) {
    const percentage = parseFloat(column.width);
    widthValue = Math.round((percentage / 100) * 1400); // Rough estimate
  } else if (typeof column.width === 'number') {
    widthValue = column.width;
  }

  return {
    field: String(column.key),
    headerName: column.label,
    width: widthValue || 150,
    minWidth: 100,
    flex: widthValue ? undefined : 1, // Use flex if no explicit width
    sortable: true,
    resizable: true,
    filterable: true,
    hideable: true,
    description: column.tooltip,
    renderCell: renderCell,
  };
}

/**
 * Creates default column visibility model
 * All columns visible by default
 */
export function createDefaultColumnVisibility<T>(
  columns: Column<T>[]
): Record<string, boolean> {
  return columns.reduce(
    (acc, col) => {
      acc[String(col.key)] = true;
      return acc;
    },
    {} as Record<string, boolean>
  );
}
